import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { authApi } from "@/lib/api/endpoints";
import { Button, Field, Input, useToast } from "@/components/ui";
import { useAuth } from "./AuthContext";
import { BrandWordmark } from "@/features/admin/components/BrandMark";

export function LoginPage() {
  const { user, login, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"login" | "forgot">("login");

  if (ready && user) {
    const from = (location.state as { from?: string } | null)?.from ?? "/admin";
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (mode === "forgot") {
        await authApi.forgotPassword(email);
        // Deliberately unconditional: the API responds the same way whether or
        // not the address exists, and so must the UI.
        toast.success("If that email is registered, reset instructions are on the way.");
        setMode("login");
      } else {
        await login(email, password);
        navigate("/admin", { replace: true });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative grid min-h-full place-items-center overflow-hidden px-4 py-12">
      <BackdropGrid />

      <div className="relative w-full max-w-[420px] animate-slide-up">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <BrandWordmark className="h-11" />
          <div className="flex flex-col gap-1.5">
            <h1 className="text-2xl font-bold tracking-tight">JebaXP Console</h1>
            <p className="text-sm text-haze-300">
              {mode === "login"
                ? "Log in to manage your Jeba event configurations"
                : "We'll email you a link to set a new password"}
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="panel flex flex-col gap-4 p-6">
          <Field label="Email address" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              autoFocus
              placeholder="admin@jeba.ai"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          {mode === "login" && (
            <Field label="Password" htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
          )}

          {error && (
            <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting} className="mt-1">
            {mode === "login" ? "Sign In to Dashboard" : "Send reset link"}
          </Button>

          <button
            type="button"
            onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setError(null); }}
            className="text-xs text-haze-400 transition-colors hover:text-accent"
          >
            {mode === "login" ? "Forgot your password?" : "Back to sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

/** Faint grid + glow, echoing the booth's dark stage look. */
function BackdropGrid() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="absolute left-1/2 top-1/3 h-[420px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-aqua/[0.06] blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 h-[320px] w-[420px] rounded-full bg-accent/[0.05] blur-[120px]" />
    </div>
  );
}
